import { Briefcase } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatCurrencyCompact } from '@/utils/formatters';
import {
  PortfolioCompanyStatus,
  type PortfolioCompanyListItem,
} from '@/types/portfolio';
import { getCountryLabel } from '@/constants/countries';
import { Ownership } from '@/components/portfolio/Ownership';
import { CapTableFinalityToggle } from '@/components/cap-table/CapTableFinalityToggle';
import type { CapTableFinality } from '@/types/captable';
import { FundValuationsContent } from './FundValuationsTab';

interface FundCompaniesTabProps {
  fundId: string;
  fundCurrency: string;
  portfolioCompanies: PortfolioCompanyListItem[];
  companiesLoading: boolean;
  companiesError: Error | null;
  finality: CapTableFinality;
  onFinalityChange: (finality: CapTableFinality) => void;
}

function statusBadgeVariant(
  status: PortfolioCompanyStatus
): 'default' | 'secondary' | 'outline' {
  if (status === PortfolioCompanyStatus.ACTIVE) return 'default';
  if (status === PortfolioCompanyStatus.EXITED) return 'secondary';
  return 'outline';
}

function PortfolioCompaniesPlaceholder({
  isLoading,
  error,
}: {
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-full"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        <p>Error loading portfolio companies</p>
        <p className="text-sm text-gray-500 mt-2">{error.message}</p>
      </div>
    );
  }
  return (
    <div className="text-center py-12 text-gray-500">
      <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
      <p>No portfolio companies found</p>
      <p className="text-sm">Add your first portfolio company to get started</p>
    </div>
  );
}

export function FundCompaniesTab({
  fundId,
  fundCurrency,
  portfolioCompanies,
  companiesLoading,
  companiesError,
  finality,
  onFinalityChange,
}: FundCompaniesTabProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab = searchParams.get('section') || 'portfolio';

  const setActiveSubTab = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('section', value);
      return newParams;
    });
  };

  return (
    <TabsContent value="companies" className="flex-1 flex flex-col">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsContent value="portfolio">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Portfolio Companies</CardTitle>
                  <CardDescription>
                    To add a company to this fund, you must now add this Fund as
                    a stakeholder directly in the{' '}
                    <Link
                      to="/app/portfolio"
                      className="text-primary hover:underline font-medium"
                    >
                      Portfolio
                    </Link>{' '}
                    Company&apos;s Cap Table.
                  </CardDescription>
                </div>
                <CapTableFinalityToggle
                  finality={finality}
                  onFinalityChange={onFinalityChange}
                />
              </div>
            </CardHeader>
            <CardContent>
              {companiesLoading ||
              companiesError ||
              portfolioCompanies.length === 0 ? (
                <PortfolioCompaniesPlaceholder
                  isLoading={companiesLoading}
                  error={companiesError}
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Ownership Type</TableHead>
                        <TableHead>Sector</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Invested</TableHead>
                        <TableHead>Valuation</TableHead>
                        <TableHead>Ownership</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {portfolioCompanies.map((company) => {
                        const isDirectOwner = company.fund_id === fundId;
                        const companyCurrency =
                          company.currency || fundCurrency;
                        return (
                          <TableRow
                            key={company.id}
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                company.receiving_fund_id
                                  ? `/app/funds/${company.receiving_fund_id}`
                                  : `/app/portfolio/${company.id}`
                              )
                            }
                          >
                            <TableCell>
                              <div className="font-medium flex items-center gap-2">
                                {company.name}
                                {company.receiving_fund_id && (
                                  <Badge variant="outline" className="text-xs">
                                    Fund
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatDate(company.initial_investment_date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={isDirectOwner ? 'default' : 'outline'}
                                className={
                                  !isDirectOwner
                                    ? 'text-slate-500 border-slate-300'
                                    : ''
                                }
                              >
                                {isDirectOwner ? 'Main Fund' : 'Stakeholder'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {company.sector || 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {company.investment_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {company.country_code
                                  ? getCountryLabel(company.country_code)
                                  : 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{companyCurrency}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">
                                {formatCurrencyCompact(
                                  company.total_invested,
                                  companyCurrency
                                )}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">
                                {company.current_valuation
                                  ? formatCurrencyCompact(
                                      company.current_valuation,
                                      companyCurrency
                                    )
                                  : 'N/A'}
                              </span>
                              {company.latest_valuation_date && (
                                <div className="text-xs text-gray-500">
                                  {formatDate(company.latest_valuation_date)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Ownership
                                fraction={company.ownership_percentage}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={statusBadgeVariant(
                                  company.current_status
                                )}
                                className="text-xs"
                              >
                                {company.current_status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="valuations">
          <FundValuationsContent fundId={fundId} fundCurrency={fundCurrency} />
        </TabsContent>
      </Tabs>
    </TabsContent>
  );
}
